import {
    type AbstractAuthenticationToken,
    type Authentication,
    type AuthenticationManager,
    AuthenticationServiceError
} from '../types.ts';
import { invalidToken } from './token-error.ts';
import { isBearerTokenAuthenticationToken, Oauth2AuthenticationError } from './token-converter.ts';

export interface Jwt extends OAuth2Token {
    readonly subject?: string

    getClaimAsString(claim: string): string | undefined
}

export type OAuth2Token = {
    readonly tokenValue: string;
    readonly issuedAt?: Date;
    readonly expiresAt?: Date;
}

type AbstractOAuth2TokenAuthenticationToken<T extends OAuth2Token> = AbstractAuthenticationToken & {
    // token: T;
}


const jwtAuthConverter = (opts?: { principalClaimName?: string}): (jwt: Jwt) => AbstractAuthenticationToken => {
    const principalClaimName = opts?.principalClaimName ?? 'sub';

    return (jwt: Jwt) => {
        const name = jwt.getClaimAsString(principalClaimName)!;
        const authentication: (AbstractOAuth2TokenAuthenticationToken<any> & { type: 'JwtToken' }) = { type: 'JwtToken', authenticated: true, name };
        return authentication;
    };
}

const asyncJwtConverter = (converter: (jwt: Jwt) => Authentication): (jwt: Jwt) => Promise<Authentication> => {
    return async (jwt: Jwt) => {
        return converter(jwt);
    };
};

export class JwtError extends Error {}
export class BadJwtError extends JwtError {}


function onError(error: JwtError) {
    if (error instanceof BadJwtError) {
        return new Oauth2AuthenticationError(invalidToken(error.message), error.message, {cause: error});
    }
    throw new AuthenticationServiceError(error.message, {cause: error});

}

export default function jwtAuthManager(opts: {
    decoder: (token: string) => Promise<Jwt>
    authConverter?: (jwt: Jwt) => Promise<Authentication>
}): AuthenticationManager {
    const decoder = opts.decoder;
    const authConverter = opts.authConverter ?? asyncJwtConverter(jwtAuthConverter({}));
    return async (authentication) => {
        if (isBearerTokenAuthenticationToken(authentication)) {
            const token = authentication.token;
            try {
                const jwt = await decoder(token);
                return await authConverter(jwt);
            } catch (e) {
                if (e instanceof JwtError) {
                    throw onError(e);
                }
                throw e;
            }
        }
    }
}
